import {
  NavReconClientAuditStatus,
  NavReconOverviewExpandedData,
  NavReconPipelineMetadata,
} from '../../../../api/rs-hg/nav-recon/types';
import { ColumnDef, type SortingState } from '@tanstack/react-table';
import {
  percentageToNdecialPlacesAddZeros,
  percentageToNdecialPlacesNoHundred,
} from '../../../../utilities/numberFormatters';
import { sortFundNames } from '../../../../utilities/customSorting';
import {
  navReconAuditStatusSortFn,
  navReconMetricStatusSortFn,
  navReconNavReceivedSortFn,
  navReconNumericColumnSort,
  navReconNumericSortFn,
  navReconStatusColumnSort,
} from './navReconTableSorting';
import NavReconOverviewMetricStatusCell from './components/NavReconOverviewMetricStatusCell';
import NavReconAuditStatusCell from './components/NavReconAuditStatusCell';
import NavReconNavReceivedTimerCell from './components/NavReconNavReceivedTimerCell';
import NavReconPipelineStagesMenu from './components/NavReconPipelineStagesMenu';
import type { NavReconFundPipelineProgress } from './pipelineMetadata';
import { formatUtcTimestampLocalTime } from '../../dashboard/utils/date.utils';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi2';
import FundNameWithDemoPill from '../../../ui/FundNameWithDemoPill';

export type NavReconOverviewTableRow = NavReconOverviewExpandedData & {
  fund_name: string;
  fund_id?: string;
  fund_name_full: string;
  subfund_code?: string;
  index: number;
  overviewHasPayload: boolean;
  auditStatus: NavReconClientAuditStatus;
  auditSnapshotTimestamp: string | null;
  auditClientComment: string | null;
  auditClientAuditByName: string | null;
  /** NAV Reconciliation Calculation complete time — starts the 2h timer. */
  navReceivedTimestamp: string | null;
  /** Epoch ms for sorting; null when calculation not complete yet. */
  navReceivedAtMs: number | null;
  /** Calculation complete timestamp (shown even when approved / timer cleared). */
  calculationTimestamp: string | null;
  /** Epoch ms for calculation-time sorting. */
  calculationAtMs: number | null;
  pipelineProgress: NavReconFundPipelineProgress;
  pipelineFundMetadata: NavReconPipelineMetadata | undefined;
  isDemoFund: boolean;
};

const STATUS_CELL_WIDTH = '10.5rem';

const formatTotalNetAssets = (v: number | undefined): string => {
  if (v === undefined || Number.isNaN(v)) return '-';
  return Math.round(v).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

/** Columns for {@link NavReconOverviewTable} (`get_nav_recon_overview2`). */
export const getNavReconOverviewColumns = (
  positionDate: string,
  section: string,
  getSorting: () => SortingState,
): ColumnDef<NavReconOverviewTableRow, any>[] => [
    {
      id: 'expand',
      header: '',
      enableSorting: false,
      meta: { align: 'center', width: '3rem' } as any,
      cell: ({ row }) => {
        const fund = row.original;
        return fund.overviewHasPayload ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
            className="rounded p-1 hover:bg-gray-100 focus:outline-none"
          >
            {row.getIsExpanded() ? (
              <HiChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <HiChevronRight className="h-4 w-4 text-gray-500" />
            )}
          </button>
        ) : (
          <div className="w-6 h-6" />
        );
      },
    },
    {
      accessorKey: 'index',
      header: '#',
      enableSorting: false,
      meta: { align: 'center', width: '3rem' } as any,
      cell: ({ row }) => <span className="text-gray-500">{row.index + 1}</span>,
    },
    {
      accessorKey: 'fund_name',
      header: 'Fund ID',
      meta: { align: 'left', minWidth: '9rem' } as any,
      cell: ({ row, getValue }) => (
        <FundNameWithDemoPill
          isDemo={row.original.isDemoFund}
          className="flex-wrap gap-y-1"
        >
          <span className="whitespace-nowrap font-semibold text-gray-900">
            {getValue() as string}
          </span>
        </FundNameWithDemoPill>
      ),
      sortingFn: (rowA, rowB) =>
        sortFundNames(rowA.original.fund_name, rowB.original.fund_name),
    },
    {
      accessorKey: 'subfund_code',
      header: 'NOIND',
      meta: { align: 'left', width: '5rem' } as any,
      cell: ({ getValue }) => (
        <span className="whitespace-nowrap text-gray-900">
          {(getValue() as string | undefined) || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'fund_name_full',
      header: 'Fund Name',
      meta: { align: 'left', minWidth: '9rem' } as any,
      cell: ({ getValue }) => (
        <span className="text-gray-900">{getValue() as string}</span>
      ),
    },
    {
      accessorKey: 'max_sc_price_return',
      header: 'Max SC Price Return',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7rem' } as any,
      cell: ({ getValue }) => percentageToNdecialPlacesNoHundred(getValue() as number, 4),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'max_sc_price_return', getSorting),
    },
    {
      accessorKey: 'min_sc_price_return',
      header: 'Min SC Price Return',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7rem' } as any,
      cell: ({ getValue }) => percentageToNdecialPlacesNoHundred(getValue() as number, 4),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'min_sc_price_return', getSorting),
    },
    {
      accessorKey: 'mean_sc_price_return',
      header: 'Mean SC Price Return',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7rem' } as any,
      cell: ({ getValue }) => percentageToNdecialPlacesNoHundred(getValue() as number, 4),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'mean_sc_price_return', getSorting),
    },
    {
      accessorKey: 'share_class_price_deviation_status',
      header: 'SC Price Deviation Status',
      ...navReconStatusColumnSort,
      meta: { align: 'center', width: STATUS_CELL_WIDTH } as any,
      cell: ({ row, getValue }) => (
        <NavReconOverviewMetricStatusCell
          auditStatus={row.original.auditStatus}
          auditClientComment={row.original.auditClientComment}
          value={getValue() as string}
          className="w-full max-w-full"
          linkToPage={`/${section}/nav-recon-share-classes?fundId=${encodeURIComponent(row.original.fund_name)}`}
        />
      ),
      sortingFn: (rowA, rowB, columnId) =>
        navReconMetricStatusSortFn(
          rowA,
          rowB,
          columnId,
          'share_class_price_deviation_status',
          getSorting,
        ),
    },
    {
      accessorKey: 'est_return',
      header: 'Est Return',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7rem' } as any,
      cell: ({ getValue }) => percentageToNdecialPlacesNoHundred(getValue() as number, 4),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'est_return', getSorting),
    },
    {
      accessorKey: 'est_return_status',
      header: 'Est Return Status',
      ...navReconStatusColumnSort,
      meta: { align: 'center', width: STATUS_CELL_WIDTH } as any,
      cell: ({ row, getValue }) => (
        <NavReconOverviewMetricStatusCell
          auditStatus={row.original.auditStatus}
          auditClientComment={row.original.auditClientComment}
          value={getValue() as string}
          linkToPage={`/${section}/nav-recon-positions?fundId=${encodeURIComponent(row.original.fund_name)}`}
          thresholdWarnings={
            row.original.over_price_change_threshold
              ? ['A position price change exceeds the threshold']
              : undefined
          }
        />
      ),
      sortingFn: (rowA, rowB, columnId) =>
        navReconMetricStatusSortFn(rowA, rowB, columnId, 'est_return_status', getSorting),
    },
    {
      accessorKey: 'unit_return_status',
      header: 'Unit Return Status',
      ...navReconStatusColumnSort,
      meta: { align: 'center', width: STATUS_CELL_WIDTH } as any,
      cell: ({ row, getValue }) => (
        <NavReconOverviewMetricStatusCell
          auditStatus={row.original.auditStatus}
          auditClientComment={row.original.auditClientComment}
          value={getValue() as string}
          linkToPage={`/${section}/nav-recon-share-classes?fundId=${encodeURIComponent(row.original.fund_name)}`}
        />
      ),
      sortingFn: (rowA, rowB, columnId) =>
        navReconMetricStatusSortFn(rowA, rowB, columnId, 'unit_return_status', getSorting),
    },
    {
      accessorKey: 'total_net_assets',
      header: 'Total Net Assets',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '8.5rem' } as any,
      cell: ({ getValue }) => formatTotalNetAssets(getValue() as number),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'total_net_assets', getSorting),
    },
    {
      accessorKey: 'volatility',
      header: 'Volatility',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '6rem' } as any,
      cell: ({ getValue }) =>
        percentageToNdecialPlacesAddZeros(getValue() as number, 2),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNumericSortFn(rowA, rowB, columnId, 'volatility', getSorting),
    },
    {
      accessorKey: 'auditStatus',
      header: 'Audit Status',
      ...navReconStatusColumnSort,
      meta: { align: 'center', width: STATUS_CELL_WIDTH } as any,
      cell: ({ row, getValue }) =>
        row.original.overviewHasPayload ? (
          <NavReconAuditStatusCell
            fundName={row.original.fund_name}
            positionDate={positionDate}
            snapshotTimestamp={row.original.auditSnapshotTimestamp}
            status={getValue() as NavReconClientAuditStatus}
            clientComment={row.original.auditClientComment}
            clientAuditByName={row.original.auditClientAuditByName}
          />
        ) : (
          <span className="text-sm text-gray-400" aria-label="No overview data">
            —
          </span>
        ),
      sortingFn: (rowA, rowB, columnId) =>
        navReconAuditStatusSortFn(rowA, rowB, columnId, getSorting),
    },
    {
      accessorKey: 'calculationTimestamp',
      header: 'Calculation Time',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '8rem' } as any,
      cell: ({ row }) => (
        <span className="tabular-nums text-sm text-gray-800">
          {formatUtcTimestampLocalTime(row.original.calculationTimestamp) ?? '—'}
        </span>
      ),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNavReceivedSortFn(rowA, rowB, columnId, getSorting),
    },
    {
      accessorKey: 'navReceivedTimestamp',
      header: 'NAV Countdown (2h)',
      ...navReconNumericColumnSort,
      meta: { align: 'center', width: '7.5rem' } as any,
      cell: ({ row }) => (
        <NavReconNavReceivedTimerCell
          receivedTimestamp={row.original.navReceivedTimestamp}
        />
      ),
      sortingFn: (rowA, rowB, columnId) =>
        navReconNavReceivedSortFn(rowA, rowB, columnId, getSorting),
    },
    {
      accessorKey: 'pipelineFundMetadata',
      header: 'Pipeline',
      enableSorting: false,
      meta: { align: 'center', width: '4.25rem' } as any,
      cell: ({ row, getValue }) => (
        <NavReconPipelineStagesMenu
          fundName={row.original.fund_name}
          metadata={getValue() as NavReconPipelineMetadata | undefined}
        />
      ),
    },
  ];
